'use client';

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import cn from 'classnames';
import { ShoppingCartIcon } from '@phosphor-icons/react';

import TextButton from '_components/UI/TextButton';
import IconButton, { type IconButtonProps } from '_components/UI/IconButton';

import styles from './QuickAddTrigger.module.scss';

export interface QuickAddTriggerProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Visible CTA label (desktop face only). */
  label: string;
  /** Compact always-visible icon face (mobile); default is the hover CTA face. */
  isMobileLayout?: boolean;
  'aria-label': string;
}

/**
 * Quick Add trigger rendered over the PLP card image. Desktop: white
 * `TextButton` CTA (Figma "Text Button") revealed on card hover/focus.
 * Mobile: always-visible `IconButton` in the image's bottom-right corner.
 * Popover props (onClick, aria-*, data-popup-open, ref) pass through, so it
 * can be used with Base UI `Popover.Trigger`'s `render` prop.
 */
const QuickAddTrigger = forwardRef<HTMLButtonElement, QuickAddTriggerProps>(
  (
    {
      label,
      isMobileLayout = false,
      className,
      'aria-label': ariaLabel,
      onClick,
      ...rest
    },
    ref
  ) => {
    if (isMobileLayout) {
      return (
        <IconButton
          {...rest}
          ref={ref}
          // IconButton's handler is typed for button|anchor; ours is button-only.
          onClick={onClick as IconButtonProps['onClick']}
          icon={ShoppingCartIcon}
          type="outline"
          size="sm"
          shape="square"
          iconSize={16}
          aria-label={ariaLabel}
          className={cn(styles.iconButton, className)}
        />
      );
    }

    return (
      <TextButton
        {...rest}
        ref={ref}
        onClick={onClick}
        type="white"
        size="small"
        label={label}
        leadingIcon={ShoppingCartIcon}
        leadingIconSize={24}
        aria-label={ariaLabel}
        className={cn(styles.cta, className)}
      />
    );
  }
);

QuickAddTrigger.displayName = 'QuickAddTrigger';

export default QuickAddTrigger;
